import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IOption, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IYearPlanningWindow, defaultValue } from 'app/shared/model/year-planning-window.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IYearPlanningWindow> = {
  loading: false,
  errorMessage: null,
  entities: [],
  options: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/OKR/year-planning-window';

const normalizeYearOptions = (data: any[]): IOption[] =>
  Array.from(
    new Set(
      data
        .map(item => {
          if (typeof item === 'number' || typeof item === 'string') {
            return Number(item);
          }

          if (typeof item?.value === 'number' || typeof item?.value === 'string') {
            return Number(item.value);
          }

          if (typeof item?.year === 'number' || typeof item?.year === 'string') {
            return Number(item.year);
          }

          return null;
        })
        .filter(year => typeof year === 'number' && !Number.isNaN(year)),
    ),
  )
    .map(year => ({
      value: year as any,
      label: String(year),
    }));

export const getYearPlanningWindowOptions = createAsyncThunk(
  'yearPlanningWindow/fetch_selectbox',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/year-planning-window-selectbox`);
  },
  { serializeError: serializeAxiosError },
);

export const getYearPlanningWindowDetail = createAsyncThunk(
  'yearPlanningWindow/fetch_detail',
  async (year: number) => {
    try {
      return await apiOkr.get<IYearPlanningWindow>(`${apiUrl}/detail-year-planning-window`, {
        params: {
          Year: year,
        },
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { data: null };
      }

      throw error;
    }
  },
  { serializeError: serializeAxiosError },
);

export const getActiveYearPlanningWindow = createAsyncThunk(
  'yearPlanningWindow/fetch_active',
  async () => {
    return apiOkr.get<IYearPlanningWindow>(`${apiUrl}/active`);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'yearPlanningWindow/create_entity',
  async (entity: IYearPlanningWindow) => {
    return apiOkr.post<IYearPlanningWindow>(`${apiUrl}/year-planning-window`, cleanEntity(entity));
  },
  { serializeError: serializeAxiosError },
);

export const changeTimeEffective = createAsyncThunk(
  'yearPlanningWindow/change_time_effective',
  async (entity: IYearPlanningWindow) => {
    return apiOkr.post<IYearPlanningWindow>(`${apiUrl}/change-time-effective`, cleanEntity(entity));
  },
  { serializeError: serializeAxiosError },
);

export const YearPlanningWindowSlice = createEntitySlice({
  name: 'yearPlanningWindow',
  initialState,
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getYearPlanningWindowOptions), (state, action) => {
        state.loading = false;
        state.options = normalizeYearOptions(action.payload.data);
      })
      .addMatcher(isFulfilled(getYearPlanningWindowDetail, getActiveYearPlanningWindow), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data || defaultValue;
      })
      .addMatcher(isFulfilled(createEntity, changeTimeEffective), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data || defaultValue;
      })
      .addMatcher(isPending(getYearPlanningWindowOptions, getYearPlanningWindowDetail, getActiveYearPlanningWindow), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, changeTimeEffective), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = YearPlanningWindowSlice.actions;

export default YearPlanningWindowSlice.reducer;
